import { useEffect } from "react";
import { CustomField } from "@measured/puck";
import { t } from "i18next";

import { Select, MenuItem, FormControl, Typography, useTheme } from "@mui/material";

import { useEntityBasedScoreParams } from "src/context/EntityBasedScore/useEntityBasedScoreParams";

import { usePuckCompanyScore } from "../contexts/PuckCompanyScoreContext";
import { withContexts } from "../wrappers/withContexts";
import NativePuckTitleField from "./NativePuckTitleField";

type ScoreSelectFieldProps = {
    field: CustomField<number | undefined>;
    name: string;
    id: string;
    value: number | null;
    onChange: (value: number | undefined) => void;
};

const PUCK_CONFIG = {
    FontFamily: "var(--puck-font-family) !important",
};

export const ScoreSelectField = (props: ScoreSelectFieldProps) => {
    const { value, onChange } = props;

    const companyContext = usePuckCompanyScore();
    const { companyPost } = companyContext;
    const theme = useTheme();

    const puckSelectStyles = {
        fontFamily: PUCK_CONFIG.FontFamily,
        fontSize: "0.875rem",

        "& .MuiSelect-select": {
            padding: theme.spacing(1.5, 2),
        },

        "& .MuiOutlinedInput-notchedOutline": {
            border: `1px solid ${theme.custom.grey[700]} !important`,
        },
    };

    const scoreQuery = useEntityBasedScoreParams({
        campaign: companyPost?.campaign?.id,
    });

    const scoreListSelect = Object.entries(companyPost?.scores ?? {}).flatMap(([scoreId, score]) => {
        if (!score.label || score.label.trim() === "") {
            return [];
        }

        return [
            {
                id: Number(scoreId),
                label: score.label,
                unit: score.unit,
                onClick: () => {
                    scoreQuery.state.selectVariation(Number(scoreId), null);
                },
            },
        ];
    });

    useEffect(() => {
        if (value === null && scoreListSelect.length > 0 && onChange) {
            onChange(scoreListSelect[0].id);
        }
    }, [scoreListSelect, value, onChange]);

    if (!companyContext) {
        return <></>;
    }

    if (scoreListSelect.length === 0) {
        return (
            <Typography variant="body2" color="text.secondary">
                {t("scores.unavailable", "No scores are available")}
            </Typography>
        );
    }

    return (
        <FormControl fullWidth>
            <NativePuckTitleField label={t("score.title", "Score")} icon="scoresIndicators" />

            <Select
                id={`score-select`}
                value={value || ""}
                displayEmpty
                onChange={(e) => onChange(Number(e.target.value))}
                sx={puckSelectStyles}
            >
                {scoreListSelect.length === 0 && (
                    <MenuItem value="" sx={{ ...puckSelectStyles, borderRadius: 0 }}>
                        <span>{t("score.none", "Select a score")}</span>
                    </MenuItem>
                )}
                {scoreListSelect.map((score) => (
                    <MenuItem key={score.id} value={score.id} sx={{ ...puckSelectStyles, borderRadius: 0 }}>
                        {score.label}
                    </MenuItem>
                ))}
            </Select>
        </FormControl>
    );
};

export default withContexts(ScoreSelectField);
